import {
  forwardRef,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import "./Modal.css";

export type StepNavPanelHandle = {
  goToStep: (index: number) => void;
  goBack: () => void;
};

export type StepNavPanelStep = {
  content: ReactNode;
  /** Shown below the sliding panels — does not move with the slide animation. */
  footer?: ReactNode;
};

export type StepNavPanelProps = {
  steps: StepNavPanelStep[];
  className?: string;
};

export const StepNavPanel = forwardRef<StepNavPanelHandle, StepNavPanelProps>(
  function StepNavPanel({ steps: stepDefs, className = "" }, ref) {
    const VIEWPORT_HEIGHT_BUFFER_PX = 12;
    const [step, setStep] = useState(0);
    const viewportRef = useRef<HTMLDivElement | null>(null);
    const panelRefs = useRef<(HTMLDivElement | null)[]>([]);

    const panels = stepDefs.map((s) => s.content);
    const stepCount = panels.length;

    const setPanelRef = (index: number) => (el: HTMLDivElement | null) => {
      panelRefs.current[index] = el;
    };

    const clampedStep =
      stepCount > 0 ? Math.min(Math.max(0, step), stepCount - 1) : 0;

    const goBack = () => setStep((s) => Math.max(0, s - 1));

    useImperativeHandle(
      ref,
      () => ({
        goToStep: (index: number) => {
          setStep(() => {
            if (stepCount === 0) return 0;
            return Math.min(Math.max(0, index), stepCount - 1);
          });
        },
        goBack,
      }),
      [stepCount],
    );

    useLayoutEffect(() => {
      if (stepCount === 0) return;

      const viewport = viewportRef.current;
      const active = panelRefs.current[clampedStep];
      if (!viewport || !active) return;

      const applyHeight = () => {
        const panel = panelRefs.current[clampedStep];
        if (!panel || !viewportRef.current) return;
        viewportRef.current.style.height = `${panel.getBoundingClientRect().height + VIEWPORT_HEIGHT_BUFFER_PX}px`;
      };

      applyHeight();

      const ro = new ResizeObserver(applyHeight);
      for (const p of panelRefs.current) {
        if (p) ro.observe(p);
      }
      return () => ro.disconnect();
    }, [stepCount, clampedStep]);

    if (stepCount === 0) {
      return null;
    }

    const rootClass = ["step-nav-viewport", className]
      .filter(Boolean)
      .join(" ");

    const footer = stepDefs[clampedStep]?.footer ?? null;

    const track =
      stepCount === 1 ? (
        <div
          className="step-nav-track"
          style={{ width: "100%", transform: "none" }}
        >
          <div
            ref={setPanelRef(0)}
            className="step-nav-panel step-nav-panel--active"
            style={{ width: "100%" }}
          >
            {panels[0]}
          </div>
        </div>
      ) : (
        <div
          className="step-nav-track"
          style={{
            width: `${stepCount * 100}%`,
            transform: `translateX(-${(clampedStep / stepCount) * 100}%)`,
          }}
        >
          {panels.map((panel, i) => (
            <div
              key={i}
              ref={setPanelRef(i)}
              className={
                i === clampedStep
                  ? "step-nav-panel step-nav-panel--active"
                  : "step-nav-panel"
              }
              style={{ width: `${100 / stepCount}%` }}
            >
              {panel}
            </div>
          ))}
        </div>
      );

    return (
      <>
        <div ref={viewportRef} className={rootClass}>
          {track}
        </div>
        {footer ? <div className="step-nav-footer">{footer}</div> : null}
      </>
    );
  },
);
